/**
 * Câmera lendo código, no navegador — o papel da vision-camera no app.
 *
 * Quem decodifica é o `BarcodeDetector`: o do próprio navegador quando existe
 * e lê QR (Chrome no Android, que usa o motor do sistema), senão o zxing em
 * WebAssembly (iPhone, Windows). O .wasm do zxing é servido junto com o PWA —
 * por padrão a biblioteca o baixaria de uma CDN, e sem rede a portaria
 * ficaria sem leitor.
 *
 * A câmera fica ligada enquanto a tela está montada e visível; `ativo` só
 * liga e desliga a decodificação. Religar a câmera a cada desfecho custaria
 * quase um segundo por pessoa na fila, e no iPhone pode voltar a pedir
 * permissão.
 */

import React, {useEffect, useRef, useState} from 'react';
import {Text, View} from 'react-native';
import {BarcodeDetector as DetectorZxing, prepareZXingModule} from 'barcode-detector/ponyfill';
import wasmDoZxing from 'zxing-wasm/reader/zxing_reader.wasm?url';

import {txt, useIdioma} from '../ui/idioma';
import {estilos, useTema} from '../ui/tema';

prepareZXingModule({
  overrides: {
    locateFile: (caminho: string, prefixo: string) =>
      caminho.endsWith('.wasm') ? wasmDoZxing : prefixo + caminho,
  },
});

/**
 * O ingresso sai em QR ou EAN-13, mas na portaria aparece de tudo: print de
 * PDF com Code-128, ingresso antigo em Code-39, cortesia em Data Matrix. A
 * regra de validação filtra o que não presta; recusar na leitura só faria o
 * operador achar que a câmera está quebrada.
 */
const FORMATOS = [
  'qr_code',
  'ean_13',
  'ean_8',
  'code_128',
  'code_39',
  'code_93',
  'itf',
  'data_matrix',
  'pdf417',
  'aztec',
] as const;

/** Intervalo entre tentativas de leitura: rápido para a fila, leve para a bateria. */
const INTERVALO_MS = 120;

interface Detector {
  detect(fonte: HTMLVideoElement): Promise<Array<{rawValue: string; format: string}>>;
}

let detectorPronto: Promise<Detector> | null = null;

function criarDetector(): Promise<Detector> {
  detectorPronto ??= (async () => {
    const Nativo = (globalThis as {BarcodeDetector?: typeof DetectorZxing}).BarcodeDetector;
    if (Nativo) {
      try {
        const suportados = await Nativo.getSupportedFormats();
        if (suportados.includes('qr_code')) {
          console.log('[camera] decodificador do navegador');
          return new Nativo({formats: FORMATOS.filter(f => suportados.includes(f))});
        }
      } catch {
        // Cai no zxing.
      }
    }
    console.log('[camera] decodificador zxing (WebAssembly)');
    return new DetectorZxing({formats: [...FORMATOS]});
  })();
  return detectorPronto;
}

export type EstadoCamera = 'iniciando' | 'ativa' | 'negada' | 'sem-camera' | 'sem-https' | 'erro';

function mensagem(estado: Exclude<EstadoCamera, 'ativa'>): string {
  switch (estado) {
    case 'iniciando':
      return txt('Abrindo a câmera…');
    case 'negada':
      return txt(
        'Permissão de câmera negada. Libere a câmera para este site nas configurações do navegador e recarregue a página.',
      );
    case 'sem-camera':
      return txt('Nenhuma câmera disponível neste aparelho.');
    case 'sem-https':
      return txt('A câmera só funciona em endereço seguro (https).');
    default:
      return txt(
        'A câmera não abriu. Feche outros apps que estejam usando a câmera e recarregue a página.',
      );
  }
}

function estadoDoErro(e: unknown): EstadoCamera {
  const nome = (e as {name?: string})?.name;
  if (nome === 'NotAllowedError' || nome === 'SecurityError') {
    return 'negada';
  }
  if (nome === 'NotFoundError' || nome === 'OverconstrainedError') {
    return 'sem-camera';
  }
  return 'erro';
}

/**
 * O fluxo da câmera sobrevive à tela. O Safari do iPhone pede permissão de
 * novo a cada `getUserMedia` depois que a câmera foi solta — sair para os
 * Setores e voltar virava um "Deseja ter acesso à câmera?" por troca de tela.
 * Então a tela devolve o fluxo em vez de pará-lo, e ele só é solto de verdade
 * depois de um tempo sem uso, ou na hora em que a página sai de frente (aí a
 * luz de câmera em uso precisa apagar, e o iOS cortaria o fluxo de qualquer
 * jeito).
 */
const SOLTAR_DEPOIS_MS = 2 * 60 * 1000;

let fluxoGuardado: MediaStream | null = null;
let relogioDeSoltar: ReturnType<typeof setTimeout> | undefined;

function fluxoVivo(fluxo: MediaStream | null): fluxo is MediaStream {
  return Boolean(fluxo?.getVideoTracks().some(tr => tr.readyState === 'live'));
}

async function pegarFluxo(): Promise<MediaStream> {
  clearTimeout(relogioDeSoltar);
  if (fluxoVivo(fluxoGuardado)) {
    return fluxoGuardado;
  }
  fluxoGuardado = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: {facingMode: {ideal: 'environment'}, width: {ideal: 1280}, height: {ideal: 720}},
  });
  return fluxoGuardado;
}

function soltarFluxo(agora: boolean) {
  clearTimeout(relogioDeSoltar);
  const parar = () => {
    fluxoGuardado?.getTracks().forEach(tr => tr.stop());
    fluxoGuardado = null;
  };
  if (agora) {
    parar();
  } else {
    relogioDeSoltar = setTimeout(parar, SOLTAR_DEPOIS_MS);
  }
}

export function LeitorCamera({
  ativo,
  lanterna,
  aoLer,
  aoMudar,
}: {
  /** Decodificando agora. A câmera continua aberta mesmo com `false`. */
  ativo: boolean;
  lanterna: boolean;
  aoLer: (valor: string, formato: string) => void;
  /** A tela precisa saber para desenhar a mira e o botão da lanterna. */
  aoMudar?: (camera: {pronta: boolean; temLanterna: boolean}) => void;
}) {
  const t = useTema();
  useIdioma();
  const video = useRef<HTMLVideoElement | null>(null);
  const trilha = useRef<MediaStreamTrack | null>(null);
  const temLed = useRef(false);
  const [estado, setEstado] = useState<EstadoCamera>('iniciando');
  const [visivel, setVisivel] = useState(
    typeof document === 'undefined' || document.visibilityState === 'visible',
  );

  const ouvinte = useRef(aoLer);
  ouvinte.current = aoLer;
  const avisarMudanca = useRef(aoMudar);
  avisarMudanca.current = aoMudar;

  // Câmera solta quando a página sai de frente — no iPhone o indicador verde
  // fica aceso, e em qualquer aparelho é bateria indo embora.
  useEffect(() => {
    const aoMudarVisibilidade = () => setVisivel(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', aoMudarVisibilidade);
    return () => document.removeEventListener('visibilitychange', aoMudarVisibilidade);
  }, []);

  useEffect(() => {
    if (!visivel) {
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      setEstado('sem-https');
      return;
    }

    let cancelado = false;
    setEstado('iniciando');

    (async () => {
      try {
        const fluxo = await pegarFluxo();
        if (cancelado) {
          return;
        }
        const tr = fluxo.getVideoTracks()[0];
        trilha.current = tr;
        if (video.current) {
          video.current.srcObject = fluxo;
          await video.current.play().catch(() => {});
        }
        const capacidades = (tr.getCapabilities?.() ?? {}) as {torch?: boolean};
        temLed.current = Boolean(capacidades.torch);
        setEstado('ativa');
        avisarMudanca.current?.({pronta: true, temLanterna: temLed.current});
      } catch (e) {
        if (!cancelado) {
          console.warn(`[camera] ${(e as Error)?.name}: ${(e as Error)?.message}`);
          setEstado(estadoDoErro(e));
          avisarMudanca.current?.({pronta: false, temLanterna: false});
        }
      }
    })();

    return () => {
      cancelado = true;
      // A lanterna não pode ficar acesa num fluxo guardado sem tela.
      if (trilha.current && temLed.current) {
        trilha.current
          .applyConstraints({advanced: [{torch: false} as MediaTrackConstraintSet]})
          .catch(() => {});
      }
      if (video.current) {
        video.current.srcObject = null;
      }
      soltarFluxo(document.visibilityState === 'hidden');
      trilha.current = null;
      avisarMudanca.current?.({pronta: false, temLanterna: false});
    };
  }, [visivel]);

  // Lanterna: só onde a câmera diz que tem (Chrome no Android). Em outro
  // aparelho o botão nem aparece — ver `temLanterna`.
  useEffect(() => {
    const tr = trilha.current;
    // Câmera sem LED recusa até o "desligar" — e o aviso sujaria o log a cada
    // abertura da tela.
    if (estado !== 'ativa' || !tr || !temLed.current) {
      return;
    }
    tr.applyConstraints({advanced: [{torch: lanterna} as MediaTrackConstraintSet]}).catch(e =>
      console.warn(`[camera] lanterna: ${e}`),
    );
  }, [lanterna, estado]);

  useEffect(() => {
    if (!ativo || estado !== 'ativa') {
      return;
    }
    let parar = false;
    let relogio: ReturnType<typeof setTimeout>;

    const tentar = async () => {
      if (parar) {
        return;
      }
      const v = video.current;
      try {
        if (v && v.readyState >= 2) {
          const detector = await criarDetector();
          const lidos = await detector.detect(v);
          const lido = lidos.find(c => c.rawValue);
          if (lido && !parar) {
            ouvinte.current(lido.rawValue, lido.format);
          }
        }
      } catch (e) {
        console.warn(`[camera] leitura falhou: ${e}`);
      }
      relogio = setTimeout(tentar, INTERVALO_MS);
    };

    tentar();
    return () => {
      parar = true;
      clearTimeout(relogio);
    };
  }, [ativo, estado]);

  return (
    <View style={{position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, backgroundColor: t.fundo}}>
      <video
        ref={video}
        muted
        playsInline
        autoPlay
        style={{width: '100%', height: '100%', objectFit: 'cover', display: estado === 'ativa' ? 'block' : 'none'}}
      />
      {estado !== 'ativa' ? (
        <View
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            bottom: 0,
            alignItems: 'center',
            justifyContent: 'center',
            padding: 24,
          }}>
          <Text style={[estilos.subtitulo, {color: t.textoFraco, textAlign: 'center', maxWidth: 420}]}>
            {mensagem(estado)}
          </Text>
        </View>
      ) : null}
    </View>
  );
}
